import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { ArrowRight, Sparkles, Clock, MapPin, Phone, Star, Scissors } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientHomePage() {
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [team, setTeam] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const locRes = await axios.get(`${API}/public/locations`);
        const locs = locRes.data || [];
        setLocations(locs);
        if (locs.length) {
          const [svcRes, provRes] = await Promise.all([
            axios.get(`${API}/public/services?location_id=${locs[0].location_id}`),
            axios.get(`${API}/public/providers?location_id=${locs[0].location_id}`),
          ]);
          setServices((svcRes.data || []).slice(0, 6));
          setTeam((provRes.data || []).slice(0, 4));
        }
      } catch (err) {
        console.error("Failed to load public site data:", err);
      }
    })();
  }, []);

  return (
    <div data-testid="client-home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-rose-50 via-white to-violet-50/60">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-rose-200/40 blur-3xl" />
          <div className="absolute top-40 -left-20 w-80 h-80 rounded-full bg-violet-200/40 blur-3xl" />
          <div className="absolute bottom-0 right-1/3 w-72 h-72 rounded-full bg-teal-100/50 blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60 backdrop-blur border border-rose-100 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-rose-500" strokeWidth={2} />
              <span className="text-xs font-semibold text-slate-700 font-['Manrope'] tracking-wide">NOW BOOKING ONLINE</span>
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05] mb-5" data-testid="home-hero-title">
              Your best look<br />starts here.
            </h1>
            <p className="text-lg text-slate-600 font-['Manrope'] leading-relaxed max-w-md mb-8">
              Book with our award-winning stylists and beauty experts. Seamless scheduling, instant confirmation, zero hassle.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/salon/book">
                <Button className="h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-7 text-base font-semibold shadow-md border-0" data-testid="hero-book-now-btn">
                  Book Appointment
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/salon/services">
                <Button variant="outline" className="h-12 border-slate-300 text-slate-800 hover:bg-slate-50 rounded-full px-7 text-base font-semibold" data-testid="hero-browse-services-btn">
                  Browse Services
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-5">
              <div className="flex -space-x-2">
                {team.slice(0, 4).map(t => (
                  <div key={t.user_id} className="w-9 h-9 rounded-full border-2 border-white shadow-sm flex items-center justify-center text-white text-xs font-semibold"
                    style={{ backgroundColor: t.color || "#B5A493" }}>
                    {t.name?.[0]}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-xs text-slate-500 font-['Manrope'] mt-0.5">Loved by 10,000+ clients</p>
              </div>
            </div>
          </div>

          {/* Visual card */}
          <div className="relative">
            <div className="relative rounded-[28px] overflow-hidden bg-white shadow-2xl border border-white/80 rotate-2">
              <div className="absolute inset-0 bg-gradient-to-br from-rose-100 via-white to-violet-100" />
              <div className="relative p-8 h-[420px] flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 font-['Manrope']">Trending</p>
                    <p className="text-2xl font-['Outfit'] font-semibold text-slate-900 mt-1">This Week</p>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center">
                    <Scissors className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="space-y-3">
                  {services.slice(0, 3).map(s => (
                    <div key={s.service_id} className="bg-white/80 backdrop-blur rounded-2xl p-4 border border-white shadow-sm flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-10 rounded-full" style={{ backgroundColor: s.color || "#F43F5E" }} />
                        <div>
                          <p className="text-sm font-semibold text-slate-900 font-['Outfit']">{s.name}</p>
                          <p className="text-xs text-slate-500 font-['Manrope']">{s.duration}min</p>
                        </div>
                      </div>
                      <p className="font-['Outfit'] text-base font-semibold text-slate-900">${s.price}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 -rotate-3 hidden sm:block">
              <p className="text-xs text-slate-400 font-['Manrope'] uppercase tracking-widest font-semibold">Instant</p>
              <p className="text-lg font-['Outfit'] font-semibold text-slate-900">Confirmation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20" data-testid="home-services-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-rose-500 font-['Manrope'] mb-2">Our Services</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Signature experiences</h2>
          </div>
          <Link to="/salon/services" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-slate-900 hover:text-rose-500 font-['Manrope']">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map(s => (
            <Link key={s.service_id} to="/salon/book" data-testid={`home-service-card-${s.service_id}`}
              className="group bg-white border border-slate-100 rounded-2xl p-6 hover:border-slate-200 hover:shadow-lg transition-all">
              <div className="w-10 h-10 rounded-xl mb-4 flex items-center justify-center" style={{ backgroundColor: `${s.color || "#F43F5E"}15` }}>
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: s.color || "#F43F5E" }} />
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 font-['Manrope'] mb-1">{s.category}</p>
              <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-2">{s.name}</h3>
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 font-['Manrope']">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{s.duration} min</span>
                </div>
                <p className="font-['Outfit'] text-lg font-semibold text-slate-900">${s.price}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Team preview */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-900 to-violet-950 text-white" data-testid="home-team-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-rose-400 font-['Manrope'] mb-2">Our Team</p>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight">Meet the pros</h2>
            </div>
            <Link to="/salon/team" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-white hover:text-rose-300 font-['Manrope']">
              Meet everyone <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {team.map(t => (
              <Link key={t.user_id} to="/salon/team" data-testid={`home-team-card-${t.user_id}`}
                className="group">
                <div className="aspect-[3/4] rounded-3xl relative overflow-hidden flex items-end p-5" style={{ background: `linear-gradient(135deg, ${t.color || "#B5A493"}, ${t.color || "#B5A493"}88)` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white font-semibold font-['Outfit']">
                    {t.name?.[0]}
                  </div>
                  <div className="relative">
                    <p className="font-['Outfit'] text-lg font-semibold leading-tight">{t.name}</p>
                    <p className="text-xs text-white/80 mt-0.5 font-['Manrope']">{t.title || "Stylist"}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Locations */}
      {locations.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20" data-testid="home-locations-section">
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-rose-500 font-['Manrope'] mb-2">Visit Us</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Our locations</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {locations.map(l => (
              <div key={l.location_id} className="bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-lg transition-all">
                <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900 mb-3">{l.name}</h3>
                <div className="space-y-2 text-sm text-slate-600 font-['Manrope']">
                  {l.address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" /><span>{l.address}</span></div>}
                  {l.phone && <div className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" /><span>{l.phone}</span></div>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-20">
        <div className="relative rounded-[32px] overflow-hidden bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 p-10 sm:p-16 text-white text-center">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22><circle cx=%2250%22 cy=%2250%22 r=%221%22 fill=%22white%22 opacity=%220.2%22/></svg>')] opacity-30" />
          <div className="relative">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Ready for your next look?</h2>
            <p className="text-white/90 font-['Manrope'] max-w-lg mx-auto mb-8">Book in under 2 minutes. Instant confirmation. We'll take care of the rest.</p>
            <Link to="/salon/book">
              <Button className="h-12 bg-white hover:bg-slate-50 text-slate-900 rounded-full px-8 text-base font-semibold shadow-xl border-0" data-testid="home-cta-book-btn">
                Book Appointment <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
